<template>
  <div class="flex flex-col gap-2 p-2">
    <div class="flex w-full items-center gap-2 p-2">
      <FontAwesomeIcon
        :icon="['far', 'magnifying-glass']"
        size="sm"
      />
      <input-text
        v-model="search_query"
        :placeholder="$t('navbar.topnavbar.selectTeam')"
        size="small"
      />
    </div>
    <div
      v-for="tenant in tenants_filtered"
      :key="`${tenant.id}-${tenant.type}`"
      class="hover:bg-gray-200"
    >
      <a :href="urlTenant(tenant)">
        <div
          :class="{ 'bg-gray-100': subdomain === tenant.attributes.subdomain }"
          class="flex cursor-pointer gap-2 p-2"
        >
          <figure
            class="image is-16x16"
            style="margin-top: 0.25rem"
          >
            <img
              :src="tenant.attributes.picture_favicon_url"
              alt="favicon-engage"
            />
          </figure>
          <div class="media-content font-medium">
            {{ tenant.attributes.name }}
          </div>
        </div>
      </a>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { mapActions, mapGetters } from '@/store/lib/vue3helpers.js';
import { RECORD_TYPE } from '@/app/data/model_constants';
import { LOGIN_PAGE_NOTIFICATIONS, VITE_ENV_REVIEW } from '@/app/data/general_constants';
import { ZIGGU_PARENT_URL, zigguProfileTenantURL } from '@/app/util/urls';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const {
  current_is_impersonation,
  current_user,
  current_user_customers,
  current_user_employees,
  current_user_partners,
  current_user_supervisors,
  current_user_projects,
  current_user_tenants,
  current_user_profiles,
} = mapGetters([
  'current_is_impersonation',
  'current_user',
  'current_user_customers',
  'current_user_employees',
  'current_user_partners',
  'current_user_supervisors',
  'current_user_projects',
  'current_user_tenants',
  'current_user_profiles',
]);

const { FETCH_USER_PROFILES, LOGOUT_USER } = mapActions(['FETCH_USER_PROFILES', 'LOGOUT_USER']);

const search_query = ref('');

const subdomain = computed(() => {
  let _subdomain = null;
  const parts = window.location.host.split('.');
  if (import.meta.env.VITE_ENV === VITE_ENV_REVIEW) {
    if (parts.length > 3) [_subdomain] = parts;
  } else if (parts.length === 3) {
    [_subdomain] = parts;
  }
  return _subdomain;
});

const profiles = computed(() => {
  const profiles_by_type = [
    Object.values(current_user_supervisors.value),
    Object.values(current_user_employees.value),
    Object.values(current_user_customers.value),
    Object.values(current_user_partners.value),
  ];
  return [].concat(...profiles_by_type);
});

const tenants_filtered = computed(() => {
  if (!current_user_tenants.value) {
    return [];
  }
  return _.filter(current_user_tenants.value, (tenant) =>
    tenant.attributes.name.toLowerCase().includes(search_query.value.toLowerCase()),
  );
});

function getProfile(tenant) {
  if (tenant.attributes.name === 'supervisor') return _.values(current_user_supervisors.value)[0];

  const client_id =
    tenant.attributes.tenantable_type === RECORD_TYPE.PROJECT
      ? current_user_projects.value[tenant.attributes.tenantable_id].attributes.client_id
      : tenant.attributes.tenantable_id;
  return _.find(profiles.value, ['attributes.client_id', client_id]);
}

function urlTenant(tenant) {
  const profile = getProfile(tenant);
  const url = zigguProfileTenantURL(profile, tenant);
  // append meta query params on impersonation for non-supervisor profiles
  if (current_is_impersonation.value && tenant.attributes.subdomain !== 'supervisor') {
    return `${url}?_profile_type=${profile.type}&_profile_id=${profile.id}`;
  }
  return url;
}

async function fetchProfiles() {
  await FETCH_USER_PROFILES({});
  if (_.size(current_user_profiles.value) === 0) {
    // retrieve and append any (present) ziggu parameters to pass onto login page
    const email = _.get(current_user.value, 'attributes.email_address', '');
    const notification = LOGIN_PAGE_NOTIFICATIONS.ERROR_NO_ACCESS;
    await LOGOUT_USER({});
    const params = [];
    if (email) params.push(`email=${email}`);
    if (notification) params.push(`notification=${notification}`);
    let url = `${ZIGGU_PARENT_URL}/login`;
    if (params.length > 0) url += `?${params.join('&')}`;
    window.location.href = url;
  }
}

// Profile fetching is now handled by parent SelectTenantModal
fetchProfiles();
</script>
